// Join code modal (§14.2): 4 letter tiles that auto-uppercase and auto-advance, paste support
// (Ctrl+V into any tile, or the PASTE button), and an error shake for invalid or unknown codes.
// Gamepad: ↑/↓ spin the focused tile through the code alphabet like arcade initials, ←/→ move.
import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH, normalizeRoomCode } from '@shared/protocol/messages';
import type { LiveScreen } from '../contracts';
import type { UiDeps } from '../deps';
import { JOIN_ERROR_TEXT } from '../labels';
import { el, replayClass, setText } from '../ui';
import { button, closeButton, setLabel, SPIN_EVENT } from '../widgets';

function lettersOnly(s: string): string {
  return [...s.toUpperCase()].filter((c) => ROOM_CODE_ALPHABET.includes(c)).join('');
}

export function create(deps: UiDeps): LiveScreen {
  const boxes = Array.from({ length: ROOM_CODE_LENGTH }, (_, i) => el('input', {
    class: 'code-tile',
    attrs: {
      type: 'text', maxlength: 1, autocomplete: 'off', spellcheck: 'false', autocapitalize: 'characters', inputmode: 'text', enterkeyhint: i === ROOM_CODE_LENGTH - 1 ? 'go' : 'next',
      'aria-label': `Letter ${i + 1}`, 'data-autofocus': i === 0, 'data-spin': true,
    },
  }));
  const error = el('div', { class: 'error-text', attrs: { 'aria-live': 'polite' } });
  const joinBtn = button({ label: 'JOIN', icon: 'play', variant: 'pink', type: 'submit' });
  let waiting = false;

  const code = (): string => boxes.map((b) => b.value).join('');
  const fill = (text: string, from: number): void => {
    const letters = lettersOnly(text);
    for (let i = 0; i < letters.length && from + i < boxes.length; i++) {
      boxes[from + i].value = letters[i];
      replayClass(boxes[from + i], 'filled');
    }
    boxes[Math.min(boxes.length - 1, from + letters.length)].focus();
    if (code().length === ROOM_CODE_LENGTH) panel.requestSubmit();
  };

  const paste = button({ label: 'PASTE', icon: 'copy', variant: 'cream', size: 'small', onClick: async () => {
    try {
      fill(await navigator.clipboard.readText(), 0);
    } catch {
      fail('Clipboard blocked — type the code instead');
    }
  } });

  const panel = el('form', { class: 'panel hero with-ribbon join-panel springy', attrs: { novalidate: true }, on: {
    submit: (e) => {
      e.preventDefault();
      if (waiting) return;
      const c = normalizeRoomCode(code());
      if (!c) return fail('Enter the 4-letter room code');
      waiting = true;
      setText(error, '');
      setLabel(joinBtn, 'JOINING…');
      joinBtn.disabled = true;
      deps.services.playUi('click');
      deps.actions.joinRoom(c);
    },
  } },
    el('div', { class: 'ribbon sky', text: 'JOIN A ROOM' }),
    closeButton(() => deps.nav.back()),
    el('p', { class: 'join-lede', text: 'Ask your host for the 4-letter code.' }),
    el('div', { class: 'code-boxes' }, ...boxes),
    el('div', { class: 'join-mid' }, error, paste),
    el('div', { class: 'row center join-actions' },
      button({ label: 'BACK', icon: 'back', variant: 'cream', onClick: () => deps.nav.back() }), joinBtn));

  function fail(msg: string): void {
    setText(error, msg);
    replayClass(panel, 'shake');
    deps.services.playUi('error');
  }

  function reset(): void {
    waiting = false;
    joinBtn.disabled = false;
    setLabel(joinBtn, 'JOIN');
  }

  boxes.forEach((box, i) => {
    box.addEventListener('input', () => {
      const v = lettersOnly(box.value);
      box.value = v.slice(-1);
      if (v) replayClass(box, 'filled');
      if (v.length > 1) fill(v.slice(1), i + 1);
      else if (v && i < boxes.length - 1) boxes[i + 1].focus();
      else if (v && code().length === ROOM_CODE_LENGTH) panel.requestSubmit();
    });
    box.addEventListener('keydown', (e) => {
      if (e.key === 'Backspace' && box.value === '' && i > 0) {
        boxes[i - 1].value = '';
        boxes[i - 1].focus();
        e.preventDefault();
      } else if (e.key === 'ArrowLeft' && i > 0) {
        boxes[i - 1].focus();
        e.preventDefault();
      } else if (e.key === 'ArrowRight' && i < boxes.length - 1) {
        boxes[i + 1].focus();
        e.preventDefault();
      }
    });
    box.addEventListener('focus', () => box.select());
    box.addEventListener('paste', (e) => {
      e.preventDefault();
      fill(e.clipboardData?.getData('text') ?? '', i);
    });
    // Gamepad letter spinner.
    box.addEventListener(SPIN_EVENT, (e) => {
      const dir = (e as CustomEvent<{ dir: number }>).detail.dir;
      const cur = ROOM_CODE_ALPHABET.indexOf(box.value);
      const next = cur < 0 ? (dir > 0 ? 0 : ROOM_CODE_ALPHABET.length - 1) : (cur + dir + ROOM_CODE_ALPHABET.length) % ROOM_CODE_ALPHABET.length;
      box.value = ROOM_CODE_ALPHABET[next];
      replayClass(box, dir > 0 ? 'spin-up' : 'spin-down');
      deps.services.playUi('hover');
    });
  });

  deps.bus.on('error', ({ code: errCode }) => {
    if (!waiting) return;
    reset();
    fail(JOIN_ERROR_TEXT[errCode] ?? 'Could not join that room');
  });

  return {
    el: el('div', { class: 'screen modal' }, panel),
    onShow() {
      for (const b of boxes) b.value = '';
      setText(error, '');
      reset();
    },
    onHide() {
      reset();
    },
  };
}
